import { devLog } from '../core/utils';
import {
  videoDemo,
  SUBMISSION_HEIGHT,
  SUBMISSION_WIDTH,
  SUBMISSION_VIDEO_DRAW_SPEED,
  SUBMISSION_IMAGE_TYPE,
  ENTRY_FORM_THUMBNAIL_CHOICES,
  errors,
} from '../core/config';


export default {
  name: 'video-preview',
  props: {
    src: {
      type: String,
      default: videoDemo, // src of the video
    },
    mark: {
      type: Number,
      default: 3, // each video has 5 marks, beginning , quarter, half, three quarters, end
    },
    runPreview: { // set to true to run the video preview
      type: Boolean,
      default: false,
    },
    rewindPreview: { // set to true to rewind the video submission to the beginning when paused
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      canvasWidth: SUBMISSION_WIDTH,
      canvasHeight: SUBMISSION_HEIGHT,
      timeout: null,
      loaded: false,
      playing: false,
      playInit: false,
      marks: [],
    };
  },

  mounted() {
    this.updateCanvasSize();
    this.init();
  },

  beforeDestroy() {
    window.clearTimeout(this.timeout);
  },

  watch: {
    /**
     * Pause or start the preview video based on prop
     *
     * @param {Boolean} value true to play the video
     */
    runPreview(value) {
      if (value) {
        this.playVideo();
        return;
      }

      this.pauseVideo();
    },
    mark: {
      handler(value) {
        this.goToMark(value);
      }
    }
  },

  methods: {
    /**
     * Check if `this.$refs` has the ref, return ref if it is found
     *
     * @param {String} ref
     *
     * @returns {Vue.Element?} element or null if not found
     */
    checkEl(ref) {
      if (!this.$refs) {
        return null;
      }
      const out = this.$refs[ref];

      if (out) {
        return out;
      }

      return null;
    },

    /**
     * Play the loaded video
     */
    playVideo() {
      const video = this.checkEl('video');

      if (!video || video.readyState !== 4) {
        this.log('Cannot play nonexistent video');
        return;
      }

      video.play().then(() => {
        this.playing = true;
        this.log('video play');
      });
    },

    /**
     * Pause the video and rewind to beginnng if playing
     */
    pauseVideo() {
      const video = this.checkEl('video');

      if (!video) {
        return;
      }

      if (this.playing) {
        video.pause();
        this.stopDraw();
        this.log('video pause');
      } else {
        this.log('video not playing, not pausing');
      }

      if (this.rewindPreview) {
        video.currentTime = 0;
      }
    },

    /**
     * Stop drawing the video to canvas
     */
    stopDraw() {
      window.clearTimeout(this.timeout);
      this.log('draw stopped');
    },

    /**
     * Update the size of the canvas,
     *
     * not using rt now
     */
    updateCanvasSize() {
      const canvas = this.checkEl('canvas');

      if (!canvas) {
        return;
      }

      this.canvasWidth = canvas.clientWidth;
      this.canvasHeight = canvas.clientHeight;

      // TODO: high res canvas
    },

    /**
     * Initialize the video ready listener
     */
    init() {
      this.log('init');
      const canvas = this.checkEl('canvas');
      const video = this.checkEl('video');

      if (!canvas || !video) {
        this.log('Init failed, no refs');
        return;
      }

      const context = canvas.getContext('2d');

      // bind handlers
      this.handleLoaded = this.handleLoaded.bind(this, video);
      this.draw = this.draw.bind(this, video, context);

      video.addEventListener('canplay', this.handleLoaded);
      video.addEventListener('error', this.handleError);
    },

    /**
     * Handle video 'canplay' event
     */
    handleLoaded(video) {
      if (this.loaded) {
        return;
      }

      this.loaded = true;
      this.log('loaded');

      video.removeEventListener('canplay', this.handleLoaded);
      video.addEventListener('play', this.draw, false);
      video.addEventListener('seeked', this.draw, false);

      // set marks
      this.setVideoMarks(video);

      this.goToMark(this.mark);

      this.draw();
    },

    /**
     * Get the timestamps for the video marks
     *
     * @param {HTMLMediaElement} video
     */
    setVideoMarks(video) {
      const {
        duration
      } = video;

      const sectionLength = duration / (ENTRY_FORM_THUMBNAIL_CHOICES - 1);

      const out = [
        0 // first mark is the beginning
      ];

      // add section marks
      for (let i = 1; i < (ENTRY_FORM_THUMBNAIL_CHOICES - 1); i += 1) {
        out.push(i * sectionLength);
      }

      // last mark is the end
      out.push(duration);

      this.marks = out;
    },

    /**
     * Forward the video to the mark, updating the canvas to this frame
     */
    goToMark(mark) {
      if (!this.loaded || this.marks.length === 0) {
        return;
      }
      const video = this.checkEl('video');

      if (this.marks[mark] === undefined) {
        throw new Error(`invalid mark: ${mark}`);
      }

      video.currentTime = this.marks[mark];
    },

    /**
     * Draw the current video frame to the canvas
     */
    draw(video, context) {
      context.drawImage(video, 0, 0, this.canvasWidth, this.canvasHeight);

      if (video.paused || video.ended) {
        this.log('draw paused');
        return;
      }
      this.timeout = setTimeout(this.draw, SUBMISSION_VIDEO_DRAW_SPEED);
    },

    /**
     * Export the current frame as a dataurl that can be loaded into a cropper
     *
     * @returns {String}
     */
    exportImage() {
      const canvas = this.checkEl('canvas');

      if (!canvas) {
        this.log('no canvas to export!');
        return null;
      }

      return canvas.toDataURL(SUBMISSION_IMAGE_TYPE);
    },

    /**
     * Export a blob of the current frame as a file that can be uploaded with submission
     *
     * @returns {Promise.<Blob>}
     */
    exportBlob() {
      const canvas = this.checkEl('canvas');

      if (!canvas) {
        this.log('no canvas to export blob!');
        return Promise.reject();
      }

      return new Promise((resolve) => {
        canvas.toBlob(
          (blob) => {
            this.$emit('submission', blob);
            return resolve(blob);
          },
          SUBMISSION_IMAGE_TYPE
        );
      });
    },

    /**
     * Handle error for video thumbnail
     *
     * @param {*} error
     */
    handleError(error) {
      this.$emit('error', { ...error, message: errors.UPLOAD_OPEN_VIDEO });
    },

    /**
     * Debug logs for video canvas.
     *
     * @param {...*} logContent whatever you want to log
     */
    log: devLog.bind(this, 'Video Canvas:', true),
  },
};
